import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchClipMetadata } from './clipMetadata';
import ScheduleDateTimePicker, { defaultScheduleDate } from './ScheduleDateTimePicker';
import { trackScheduleClipDialogSubmitted } from './userJourneyTracker';
import './ScheduleClipModal.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

const PLATFORM_ICONS = {
  facebook: `${PUBLIC_URL}/Facebook.png`,
  instagram: `${PUBLIC_URL}/instagram.svg`,
  linkedin: `${PUBLIC_URL}/linkedin.svg`,
  youtube: `${PUBLIC_URL}/YouTube.svg`,
};

const PLATFORMS = [
  { id: 'youtube', label: 'YouTube' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'linkedin', label: 'LinkedIn' },
];

function displayProjectName(project) {
  return (project.file_name || project.file_stem || '').replace(/\.[^.]+$/, '');
}

function AuthThumbnail({ url, getFreshTokenRef, className, alt = '' }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getFreshTokenRef.current();
        if (!token || cancelled) return;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled) return;
        setSrc(URL.createObjectURL(blob));
      } catch {
        /* ignore */
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [url, getFreshTokenRef]);

  if (!src) {
    return <div className={`${className} schedule-modal__thumb-placeholder`} aria-hidden="true" />;
  }
  return <img src={src} alt={alt} className={className} loading="lazy" />;
}

export default function ScheduleClipModal({
  isOpen,
  onClose,
  accounts = [],
  getFreshTokenRef,
  initialScheduleDate = null,
  initialFileStem = '',
  initialClipFilename = '',
  onScheduled,
}) {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [clips, setClips] = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [selectedClip, setSelectedClip] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState(() => defaultScheduleDate());
  const [publishNow, setPublishNow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [metadataLoadingField, setMetadataLoadingField] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const metadataGenerationRef = useRef(0);

  const connectedAccounts = accounts.filter((a) => a.status === 'active');
  const connectedPlatformIds = connectedAccounts.map((a) => a.platform);

  useEffect(() => {
    if (!isOpen) return undefined;
    setTitle('');
    setDescription('');
    setError('');
    setSuccessMessage('');
    setScheduledAt(defaultScheduleDate(initialScheduleDate));
    setPublishNow(false);
    metadataGenerationRef.current += 1;
    setMetadataLoadingField('');
    setSelectedPlatforms(connectedPlatformIds);

    if (initialFileStem && initialClipFilename) {
      setSelectedProject({ file_stem: initialFileStem, file_name: initialFileStem });
      setSelectedClip(null);
      setClips([]);
    } else {
      setSelectedProject(null);
      setSelectedClip(null);
      setClips([]);
    }
  }, [
    isOpen,
    initialScheduleDate,
    initialFileStem,
    initialClipFilename,
    connectedPlatformIds.join('|'),
  ]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;

    async function loadProjects() {
      setProjectsLoading(true);
      try {
        const token = await getFreshTokenRef.current();
        if (!token || cancelled) return;
        const res = await fetch('/api/user/projects?limit=5', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) {
          let nextProjects = data.projects || [];
          if (
            initialFileStem
            && !nextProjects.some((project) => project.file_stem === initialFileStem)
          ) {
            nextProjects = [
              { file_stem: initialFileStem, file_name: initialFileStem },
              ...nextProjects,
            ];
          }
          setProjects(nextProjects);
        }
      } catch {
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    }

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [isOpen, getFreshTokenRef, initialFileStem]);

  useEffect(() => {
    if (!isOpen || !selectedProject?.file_stem) {
      setClips([]);
      setSelectedClip(null);
      return undefined;
    }

    let cancelled = false;

    async function loadClips() {
      setClipsLoading(true);
      setSelectedClip(null);
      try {
        const token = await getFreshTokenRef.current();
        if (!token || cancelled) return;
        const res = await fetch(
          `/api/user/projects/${encodeURIComponent(selectedProject.file_stem)}/clips`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) {
          const clipList = data.clips || [];
          setClips(clipList);
          if (initialClipFilename) {
            const match = clipList.find((clip) => clip.filename === initialClipFilename);
            if (match) setSelectedClip(match);
          }
        }
      } catch {
        if (!cancelled) setClips([]);
      } finally {
        if (!cancelled) setClipsLoading(false);
      }
    }

    loadClips();
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedProject, getFreshTokenRef, initialClipFilename]);

  useEffect(() => {
    if (!selectedClip || !selectedProject) return undefined;

    const generationId = metadataGenerationRef.current + 1;
    metadataGenerationRef.current = generationId;
    setTitle('');
    setDescription('');
    setMetadataLoadingField('all');
    setError('');

    let cancelled = false;

    async function loadMetadata() {
      try {
        const meta = await fetchClipMetadata(getFreshTokenRef.current, {
          fileStem: selectedProject.file_stem,
          clipFilename: selectedClip.filename,
          generate: true,
          field: 'all',
        });
        if (cancelled || metadataGenerationRef.current !== generationId) return;
        setTitle(meta.title || '');
        setDescription(meta.description || '');
      } catch (err) {
        if (cancelled || metadataGenerationRef.current !== generationId) return;
        setTitle(selectedClip.filename.replace(/\.[^.]+$/, '').replace(/_/g, ' '));
        setDescription('');
        setError(err.message || 'Could not generate title and description.');
      } finally {
        if (!cancelled && metadataGenerationRef.current === generationId) {
          setMetadataLoadingField('');
        }
      }
    }

    loadMetadata();
    return () => {
      cancelled = true;
    };
  }, [selectedClip?.filename, selectedProject?.file_stem, getFreshTokenRef]);

  const regenerateMetadataField = async (field) => {
    if (!selectedProject?.file_stem || !selectedClip?.filename || metadataLoadingField) return;
    setMetadataLoadingField(field);
    setError('');
    try {
      const meta = await fetchClipMetadata(getFreshTokenRef.current, {
        fileStem: selectedProject.file_stem,
        clipFilename: selectedClip.filename,
        generate: true,
        field,
      });
      if (field === 'title') {
        setTitle(meta.title || '');
      } else if (field === 'description') {
        setDescription(meta.description || '');
      }
    } catch (err) {
      setError(err.message || `Could not regenerate ${field}.`);
    } finally {
      setMetadataLoadingField('');
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, submitting, onClose]);

  const togglePlatform = (platformId) => {
    setSelectedPlatforms((current) =>
      current.includes(platformId)
        ? current.filter((id) => id !== platformId)
        : [...current, platformId],
    );
  };

  const handleSubmit = async () => {
    setError('');
    setSuccessMessage('');

    if (!selectedProject?.file_stem || !selectedClip?.filename) {
      setError('Select a project and clip first.');
      return;
    }
    if (selectedPlatforms.length === 0) {
      setError('Choose at least one connected platform.');
      return;
    }
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Add a title before publishing.');
      return;
    }

    let scheduledAtUtc = null;
    if (!publishNow) {
      const localDate = scheduledAt;
      if (!localDate || Number.isNaN(localDate.getTime())) {
        setError('Pick a valid schedule time.');
        return;
      }
      if (localDate <= new Date()) {
        setError('Schedule time must be in the future.');
        return;
      }
      scheduledAtUtc = localDate.toISOString();
    }

    trackScheduleClipDialogSubmitted({
      post_now: publishNow,
      platforms: selectedPlatforms.join(','),
      file_stem: selectedProject.file_stem,
      clip_filename: selectedClip.filename,
    });

    setSubmitting(true);
    try {
      const token = await getFreshTokenRef.current();
      if (!token) throw new Error('Please sign in again.');

      const payload = {
        post_now: publishNow,
        file_stem: selectedProject.file_stem,
        clip_filename: selectedClip.filename,
        scheduled_at: scheduledAtUtc,
        metadata: {
          title: trimmedTitle,
          description: description.trim(),
          hashtags: [],
        },
      };

      const results = await Promise.allSettled(
        selectedPlatforms.map(async (platform) => {
          const res = await fetch('/api/schedule', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ...payload, platform }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data.error || `Failed for ${platform}`);
          }
          return platform;
        }),
      );

      const failed = results.filter((r) => r.status === 'rejected');
      const succeeded = results.filter((r) => r.status === 'fulfilled');

      if (succeeded.length === 0) {
        throw new Error(failed[0]?.reason?.message || 'Scheduling failed.');
      }

      const label = succeeded.map((r) => r.value).join(', ');
      const actionLabel = publishNow ? 'Published' : 'Scheduled';
      setSuccessMessage(
        failed.length
          ? `${actionLabel} on ${label}. ${failed.length} platform(s) failed.`
          : `${actionLabel} on ${label}.`,
      );
      onScheduled?.();
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err.message || (publishNow ? 'Could not publish clip.' : 'Could not schedule clip.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="schedule-modal-overlay" role="presentation" onClick={submitting ? undefined : onClose}>
      <div
        className="schedule-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-clip-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="schedule-modal__header">
          <h2 id="schedule-clip-title" className="schedule-modal__title">Schedule clip</h2>
          <button
            type="button"
            className="schedule-modal__close"
            aria-label="Close"
            disabled={submitting}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="schedule-modal__body">
          <section className="schedule-modal__section">
            <h3 className="schedule-modal__section-title">Where to post</h3>
            <p className="schedule-modal__hint">Select one or more connected channels.</p>
            <div className="schedule-modal__platforms">
              {PLATFORMS.map((platform) => {
                const connected = connectedAccounts.some((a) => a.platform === platform.id);
                const selected = selectedPlatforms.includes(platform.id);
                return (
                  <button
                    key={platform.id}
                    type="button"
                    className={[
                      'schedule-modal__platform-chip',
                      connected && 'is-connected',
                      selected && 'is-selected',
                      !connected && 'is-disabled',
                    ].filter(Boolean).join(' ')}
                    disabled={!connected || submitting}
                    onClick={() => togglePlatform(platform.id)}
                    aria-pressed={selected}
                  >
                    <img src={PLATFORM_ICONS[platform.id]} alt="" width={18} height={18} aria-hidden="true" />
                    <span>{platform.label}</span>
                  </button>
                );
              })}
            </div>
            {connectedAccounts.length === 0 && (
              <p className="schedule-modal__warn">Connect a platform in the sidebar first.</p>
            )}
          </section>

          <section className="schedule-modal__section">
            <h3 className="schedule-modal__section-title">Select project</h3>
            {projectsLoading ? (
              <p className="schedule-modal__hint">Loading projects…</p>
            ) : projects.length === 0 ? (
              <p className="schedule-modal__hint">No past projects with clips yet.</p>
            ) : (
              <div className="schedule-modal__projects">
                {projects.map((project) => (
                  <button
                    key={project.file_stem}
                    type="button"
                    className={[
                      'schedule-modal__project-card',
                      selectedProject?.file_stem === project.file_stem && 'is-selected',
                    ].filter(Boolean).join(' ')}
                    onClick={() => setSelectedProject(project)}
                  >
                    <div className="schedule-modal__project-thumb-wrap">
                      <AuthThumbnail
                        url={project.thumbnail_url}
                        getFreshTokenRef={getFreshTokenRef}
                        className="schedule-modal__project-thumb"
                      />
                    </div>
                    <span className="schedule-modal__project-name">{displayProjectName(project)}</span>
                  </button>
                ))}
              </div>
            )}
          </section>

          {selectedProject && (
            <section className="schedule-modal__section">
              <h3 className="schedule-modal__section-title">Select clip</h3>
              {clipsLoading ? (
                <p className="schedule-modal__hint">Loading clips…</p>
              ) : clips.length === 0 ? (
                <p className="schedule-modal__hint">No clips found for this project.</p>
              ) : (
                <div className="schedule-modal__clips">
                  {clips.map((clip) => (
                    <button
                      key={clip.filename}
                      type="button"
                      className={[
                        'schedule-modal__clip-card',
                        selectedClip?.filename === clip.filename && 'is-selected',
                      ].filter(Boolean).join(' ')}
                      onClick={() => setSelectedClip(clip)}
                    >
                      <video
                        src={clip.url}
                        className="schedule-modal__clip-video"
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <span className="schedule-modal__clip-name">{clip.filename}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {selectedClip && (
            <section className="schedule-modal__section schedule-modal__section--fields">
              {metadataLoadingField === 'all' && (
                <p className="schedule-modal__hint">Generating title and description…</p>
              )}
              <label className="schedule-modal__field">
                <div className="schedule-modal__field-label-row">
                  <span>Title</span>
                  <button
                    type="button"
                    className="schedule-modal__regenerate-btn"
                    disabled={Boolean(metadataLoadingField) || submitting}
                    onClick={() => regenerateMetadataField('title')}
                  >
                    {metadataLoadingField === 'title' ? 'Generating…' : '↻ Regenerate'}
                  </button>
                </div>
                <input
                  type="text"
                  value={title}
                  maxLength={100}
                  disabled={submitting || Boolean(metadataLoadingField)}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label className="schedule-modal__field">
                <div className="schedule-modal__field-label-row">
                  <span>Description</span>
                  <button
                    type="button"
                    className="schedule-modal__regenerate-btn"
                    disabled={Boolean(metadataLoadingField) || submitting}
                    onClick={() => regenerateMetadataField('description')}
                  >
                    {metadataLoadingField === 'description' ? 'Generating…' : '↻ Regenerate'}
                  </button>
                </div>
                <textarea
                  value={description}
                  rows={3}
                  disabled={submitting || Boolean(metadataLoadingField)}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <div className="schedule-modal__field">
                <span>Publish at</span>
                <ScheduleDateTimePicker
                  value={scheduledAt}
                  onChange={setScheduledAt}
                  disabled={submitting}
                  showNowOption
                  publishNow={publishNow}
                  onPublishNowChange={setPublishNow}
                />
              </div>
            </section>
          )}

          {error && (
            <div className="schedule-modal__error" role="alert">{error}</div>
          )}
          {successMessage && (
            <div className="schedule-modal__success" role="status">{successMessage}</div>
          )}
        </div>

        <footer className="schedule-modal__footer">
          <button type="button" className="cal-btn cal-btn-outline" disabled={submitting} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="cal-btn cal-btn-primary"
            disabled={submitting || Boolean(metadataLoadingField) || !selectedClip || selectedPlatforms.length === 0}
            onClick={handleSubmit}
          >
            {submitting
              ? (publishNow ? 'Posting…' : 'Scheduling…')
              : (publishNow ? 'Post now' : 'Schedule clip')}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
